import { request } from './axiosConfig';
import { keysToCamel } from '../utils';

const baseUrl = 'api/connector';

// Error responses only carry a return url for redirect-type errors. Keep the
// connector's return_uri already in state when the response has none, so the
// error page's Cancel button still has a destination.
export const returnUriFromError = (response) => {
  const url = response?.data?.url;
  return url ? { returnUri: url } : {};
};

// The header shows the connector's logo from sessionStorage on pages that do
// not load the connector themselves. A missing logo used to be stored as the
// string "null", which those pages rendered as a broken image.
export const storeClientLogo = (url) => {
  if (url) {
    sessionStorage.setItem('clientLogo', url);
  } else {
    sessionStorage.removeItem('clientLogo');
  }
};

export const checkIsValidState = ({ updateState, clientId, state }) =>
  request
    .get(`${baseUrl}/connector/client/${clientId}/state/${state}`)
    .then(() => {
      updateState({ error: false, isValidState: true, isLoading: true });
    })
    .catch(({ response }) => {
      updateState({
        errorAction: response?.data?.action,
        ...returnUriFromError(response),
        error: true,
        isValidState: false,
        isLoading: false,
      });
    });

export const getConnectorData = ({ updateState, clientId }) =>
  request
    .get(`${baseUrl}/connector/client/${clientId}`)
    .then((response) => {
      storeClientLogo(response.data.client_logo_url);
      updateState({
        ...keysToCamel(response.data),
        isLoading: false,
      });
    })
    .catch(({ response }) => {
      updateState({
        errorAction: response?.data?.action,
        ...returnUriFromError(response),
        isLoading: false,
        error: true,
      });
    });

export const getAuthorizeUrl = ({ updateState, requestBody }) =>
  request
    .post(`${baseUrl}/connection`, requestBody)
    .then((response) => {
      updateState({
        preStartConnection: {
          ...keysToCamel(response.data),
          isReadyToConnect: true,
        },
        error: false,
      });
    })
    .catch(({ response }) => {
      updateState({
        preStartConnection: {
          isReadyToConnect: false,
        },
        error: true,
        errorAction: response?.data?.action,
        ...returnUriFromError(response),
        errorMessage: response?.data?.error_message,
      });
    });

export const setConnectionData = ({ updateState, requestBody }) =>
  request
    .post(`${baseUrl}/auth/obtain-access-token/`, requestBody)
    .then((response) => {
      updateState({
        afterConnection: {
          ...keysToCamel(response.data),
        },
        error: false,
      });
    })
    .catch(({ response }) => {
      updateState({
        afterConnection: {},
        error: true,
        errorAction: response?.data?.action,
        ...returnUriFromError(response),
        errorMessage: response?.data?.error_message,
      });
    });

export const getChosenApplication = ({ updateState, clientId, applicationId }) =>
  request
    .get(`${baseUrl}/connector/client/${clientId}/${applicationId}`)
    .then((response) => {
      storeClientLogo(response.data.client_logo_url);
      updateState({
        ...keysToCamel(response.data),
        isLoading: false,
      });
    })
    .catch(({ response }) => {
      updateState({
        errorAction: response?.data?.action,
        ...returnUriFromError(response),
        isLoading: false,
        error: true,
      });
    });

// The result page skips the state check and so never loads the connector; it
// only needs the logo for the header, and a failure must not hide the result.
export const getConnectorLogo = ({ updateState, clientId }) =>
  request
    .get(`${baseUrl}/connector/client/${clientId}`)
    .then((response) => {
      const url = response.data.client_logo_url || null;
      storeClientLogo(url);
      updateState({ clientLogoUrl: url });
    })
    .catch(() => {});

export const getOnboardingResult = ({ updateState, clientId, state }) =>
  request
    .get(`${baseUrl}/connector/client/${clientId}/onboarding-result/${state}`)
    .then((response) => {
      const onboardingResult = keysToCamel(response.data);
      updateState({
        onboardingResult,
        returnUri: onboardingResult.returnUri,
        error: false,
        isLoading: false,
      });
    })
    .catch(({ response }) => {
      updateState({
        errorAction: response?.data?.action,
        ...returnUriFromError(response),
        errorMessage: response?.data?.error_message,
        isLoading: false,
        error: true,
      });
    });

// TODO: Future implementation: Receive trackingId and app data on /step-authorized
export const getTrackingInformation = () => {};

export const setTrackingInformation = ({ requestBody, headers = {} }) => {
  request
    .post(`${baseUrl}/tracking-event`, requestBody, {
      headers: {
        'X-CLIENT-ID': headers.clientId,
        'X-TRACKING-ID': headers.trackingId,
      },
    })
    .then(() => {})
    .catch(({ response }) => {
      console.error(response?.data?.error_message);
    });
};

export const setTrackingInformationByBeacon = ({ requestBody }) => {
  request
    .post(`${baseUrl}/tracking-event`, requestBody)
    .then(() => {})
    .catch(({ response }) => {
      console.error(response?.data?.error_message);
    });
};
